import { Injectable } from '@angular/core';
import { HttpRequestService } from '../shared/services/http-request.service';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { formatUrl } from '../shared/utils/format-url-util';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { NotificationService } from '../shared/services/notification.service';

@Injectable({
  providedIn: 'root'
})
export class PurchaseRequestService {

  constructor(
    private httpClient: HttpRequestService,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService: NotificationService
  ) { }

  approveRejectPurchaseRequest(payload: any, programCode: string, notificationStatus: string, duplicateErrorCodeMessage: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.PURCHASE_REQUEST.APPROVE_REJECT_PURCHASE_REQUEST, { program_code: programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap((response: any) => {
          if (response?.responsemsg && Array.isArray(response.responsemsg) && response.responsemsg[0]?.code !== 'PURCHASE_REQUEST_ERROR') {
            this.notificationService.success(
              'Success',
              `Purchase request ${notificationStatus} successfully.`
            );
          }
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  /**
   * Purchase request lines drawn against one authorization — the purchase-request half of the
   * utilization view, alongside getAuthTimesheetUtilization and getAuthInvoiceUtilization.
   */
  getAuthPurchaseRequestUtilization(programCode: string, authId: string, participantId: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.PURCHASE_REQUEST.GET_AUTH_PURCHASE_REQUEST_UTILIZATION, {
      programCode, participantId, authId
    });

    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getParticipantInfo(program_code: string, participant_ppl_id: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.PURCHASE_REQUEST.GET_PARTICIPANT_INFO, { program_code, participant_ppl_id });

    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getAdjustmentParentLines(programCode: string, lineDetailId: any, type = 'PR'): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_ADJUST_PARENT_LINES, { programCode, lineDetailId, type });

    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getPurchaseRequest(program_code: string, tas_purchaserequest_detial_id: any, isAfterUpdate = false): Observable<any> {
    const url = formatUrl(ENDPOINTS.PURCHASE_REQUEST.GET_PURCHASE_REQUEST, { program_code, tas_purchaserequest_detial_id });

    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  savePurchaseRequest(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.PURCHASE_REQUEST.SAVE_PURCHASE_REQUEST, { program_code: programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap((response: any) => {
          if (response?.responsedata !== 'Purchase Request Auth Error') {
            this.notificationService.success(
              'Success',
              `Purchase request submitted successfully.`
            );
          }

          return response;
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  adjustPurchaseRequest(endpointUrl: string, programCode: string, payload: any, placeholder: string): Observable<any> {
    const url = formatUrl(endpointUrl, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `${placeholder} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }
}
